// practitioner-list.component.ts - Updated with dialog and fixed API calls

import { Component, inject, ViewChild, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { Practitioner } from 'fhir/r4';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { baseStatusStyles } from '../shared/statusUIIcons';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { AuthService } from '../shared/auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { authBackendEndPointToken } from '../app.config';
import { PractitionerDetailsComponent } from '../practitioner-details/practitioner-details.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogData } from './confirm-action-dialog.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { AgePipe } from '../age.pipe';
import { ErrorService } from '../shared/error.service';
import { PractitionerEditDialogComponent } from './practitioner-edit-dialog.component';
import { JoinRequestsComponent } from './join-requests.component';




export interface PractitionerWithStatus extends Practitioner {
  status?: 'pending' | 'approved' | 'archived';
  userId?: string;
  email?: string;
  isApproved?: boolean;
  phoneNumber?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
  submittedAt?: string;
  registrationDate?: string;
  fhirResourceId?: string;
}

@Component({
    selector: 'app-practitioner-list',
    standalone: true,
    imports: [
        TableHeaderComponent, MatMenuModule, AgePipe, MatTabsModule,
        RouterModule, CommonModule, MatCardModule, MatButtonModule,
        MatIconModule, MatTableModule, MatDividerModule, EmptyStateComponent,
        MatSortModule,JoinRequestsComponent
    ],
    templateUrl: './practitioner-list.component.html',
    styleUrl: './practitioner-list.component.scss'
})
export class PractitionerListComponent implements AfterViewInit {
    statusStyles = baseStatusStyles;
    currentSegment: 'pending' | 'approved' | 'archived' = 'pending';

    immutableTableData: PractitionerWithStatus[] = [];
    tableDataSource = new MatTableDataSource<PractitionerWithStatus>();
    tableDataLevel2 = new BehaviorSubject<PractitionerWithStatus[]>([]);
showRequestsTab = false;
joinRequestCount = 0;
    tableFilter = new Map<string, any[]>();
    tableFilterFormControlObject: { [key: string]: FormGroup } = {};
    tableColumns: string[] = [];
    private currentSegmentRows: PractitionerWithStatus[] = [];
    private activeFilters: Record<string, string[]> = {};
    activeSearchTerm = '';
    activeSearchField: string | null = null;
    searchableFields: { label: string; value: string }[] = [];
    selectedSearchField: string | null = null;

    pageTitle = '';
    pageSubtitle = '';
    isLoading = false;

    @ViewChild(MatSort) sort!: MatSort;

    http = inject(HttpClient);
    route = inject(ActivatedRoute);
    router = inject(Router);
    dialog = inject(MatDialog);
    auth = inject(AuthService);
    _sn = inject(MatSnackBar);
    backendApi = inject(authBackendEndPointToken);
    errorService = inject(ErrorService);

    ngOnInit() {
        this.configureHeaderForSegment(this.currentSegment);
        this.setupTableColumns();
        this.connectTableDataSource();
        this.loadPractitioners();
    }

    ngAfterViewInit() {
        this.setupSorting();
    }

    private setupSorting() {
        this.tableDataSource.sort = this.sort;

        this.tableDataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'registration date':
                    const dateStr = item.submittedAt || item.createdAt || item.registrationDate;
                    return dateStr ? new Date(dateStr).getTime() : 0;
                
                case 'name':
                    return item.fullName || 
                           `${item.firstName || ''} ${item.lastName || ''}`.trim() ||
                           `${item.name?.[0]?.given?.[0] || ''} ${item.name?.[0]?.family || ''}`.trim();
                
                case 'contact':
                    return item.email || item.phoneNumber || item.telecom?.[0]?.value || '';
                
                case 'status':
                    return item.status || '';
                
                default:
                    return (item as any)[property] || '';
            }
        };

        setTimeout(() => {
            if (this.sort) {
                this.sort.sort({
                    id: 'registration date',
                    start: 'desc',
                    disableClear: false
                });
            }
        });
    }

    private configureHeaderForSegment(segment: 'pending' | 'approved' | 'archived') {
        this.activeFilters = {};
        this.tableFilter = this.getFilterMapForSegment(segment);
        this.tableFilterFormControlObject = this.buildFilterControls(this.tableFilter);
        this.searchableFields = this.getSearchableFieldsForSegment(segment);
        this.selectedSearchField = this.searchableFields[0]?.value ?? null;
        this.activeSearchField = this.selectedSearchField;
    }

    private getFilterMapForSegment(segment: 'pending' | 'approved' | 'archived'): Map<string, string[]> {
        if (segment === 'approved') {
            return new Map([
                ['gender', ['male', 'female', 'other', 'unknown']]
            ]);
        }

        return new Map();
    }

    private buildFilterControls(filters: Map<string, string[]>): { [key: string]: FormGroup } {
        const controls: { [key: string]: FormGroup } = {};
        for (const [key, value] of filters) {
            controls[key] = new FormGroup({});
            for (const filterValue of value) {
                controls[key].addControl(filterValue, new FormControl(false));
            }
        }
        return controls;
    }

    private getSearchableFieldsForSegment(segment: 'pending' | 'approved' | 'archived'): { label: string; value: string }[] {
        if (segment === 'approved') {
            return [
                { label: 'Practitioner ID', value: 'practitionerId' },
                { label: 'Name', value: 'name' },
                { label: 'Email', value: 'email' },
                { label: 'Phone', value: 'phone' },
                { label: 'Gender', value: 'gender' }
            ];
        }

        return [
            { label: 'Name', value: 'name' },
            { label: 'Email', value: 'email' },
            { label: 'Phone', value: 'phone' }
        ];
    }

    private setupTableColumns() {
        this.tableColumns = [
            'name',
            'contact',
            'registration date',
            'status',
            'action',
        ];
    }

    private connectTableDataSource() {
        this.tableDataSource.connect = () => this.tableDataLevel2;
    }

    private createPractitionerFromAdminData(user: any, status: 'pending' | 'approved' | 'archived'): PractitionerWithStatus | null {
        const practitioner = user.practitioner ?? {};
        const roleSpecificData = user.roleSpecificData ?? {};
        const firstName = user.firstName || this.readFhirPrimitive(practitioner.name?.[0]?.given?.[0]);
        const lastName = user.lastName || this.readFhirPrimitive(practitioner.name?.[0]?.family);
        const fullName =
            user.fullName ||
            this.readFhirPrimitive(practitioner.name?.[0]?.text) ||
            `${firstName || ''} ${lastName || ''}`.trim();
        const email = user.email || this.getPractitionerTelecomValue(practitioner, 'email');
        const phoneNumber = user.phoneNumber || this.getPractitionerTelecomValue(practitioner, 'phone');
        const gender = this.readFhirPrimitive(user.gender ?? roleSpecificData.gender ?? practitioner.gender);
        const birthDate = this.readFhirPrimitive(user.birthDate ?? roleSpecificData.birthDate ?? practitioner.birthDate);

        if (!email && !phoneNumber && !fullName && !firstName && !lastName) {
            return null;
        }

        const telecom: any[] = [];
        if (email) {
            telecom.push({
                system: 'email',
                value: email,
                use: 'work'
            });
        }
        if (phoneNumber) {
            telecom.push({
                system: 'phone',
                value: phoneNumber,
                use: 'work'
            });
        }

        const registrationDate = user.submittedAt || user.createdAt;

        return {
            resourceType: 'Practitioner',
            id: user.fhirResourceId || user.id,
            name: [{
                family: lastName || '',
                given: firstName ? [firstName] : [],
                text: fullName
            }],
            telecom: telecom.length > 0 ? telecom : undefined,
            gender: gender as any,
            birthDate,
            status: status,
            userId: user.id,
            email,
            phoneNumber,
            fullName,
            firstName,
            lastName,
            createdAt: user.createdAt,
            submittedAt: user.submittedAt,
            registrationDate: registrationDate,
            isApproved: status === 'approved',
            fhirResourceId: user.fhirResourceId  // ✅ Ensure this is set
        };
    }

    private readFhirPrimitive(value: any): string {
        if (value === undefined || value === null) {
            return '';
        }
        if (typeof value === 'object' && 'value' in value) {
            return String(value.value ?? '');
        }
        return String(value);
    }

    private getPractitionerTelecomValue(practitioner: any, system: string): string {
        const telecom = Array.isArray(practitioner?.telecom) ? practitioner.telecom : [];
        const match = telecom.find((item: any) => this.readFhirPrimitive(item?.system) === system);
        return this.readFhirPrimitive(match?.value);
    }

  loadPractitioners() {
        this.isLoading = true;

        forkJoin({
            pending: this.http.get<any[]>(`${this.backendApi}/api/admin/approvals/pending?accountType=Practitioner`),
            approved: this.http.get<any[]>(`${this.backendApi}/api/admin/practitioners`),
            archived: this.http.get<any[]>(`${this.backendApi}/api/admin/practitioners/archived`)
        }).subscribe({
            next: (results) => {
                const allPractitioners: PractitionerWithStatus[] = [];

                for (const approval of results.pending) {
                    const practitioner = this.createPractitionerFromAdminData(approval, 'pending');
                    if (practitioner) {
                        allPractitioners.push(practitioner);
                    }
                }

                for (const user of results.approved) {
                    if (user.isApproved) {
                        const practitioner = this.createPractitionerFromAdminData(user, 'approved');
                        if (practitioner) {
                            allPractitioners.push(practitioner);
                        }
                    }
                }

                for (const user of results.archived) {
                    const practitioner = this.createPractitionerFromAdminData(user, 'archived');
                    if (practitioner) {
                        allPractitioners.push(practitioner);
                    }
                }

                this.immutableTableData = allPractitioners;
                this.filterBySegment(this.currentSegment);
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Failed to load practitioners:', error);
                this.errorService.openandCloseError('Failed to load practitioners');
                this.isLoading = false;
            }
        });
    }

    onTabChange(index: number) {
      this.showRequestsTab = index === 3;
    if (index > 2) {
        return;  
    }
        const segments: Array<'pending' | 'approved' | 'archived'> = ['pending', 'approved', 'archived'];
        this.currentSegment = segments[index];
        this.configureHeaderForSegment(this.currentSegment);
        this.filterBySegment(this.currentSegment);
        this.updateColumnsForSegment(this.currentSegment);
        
        setTimeout(() => {
            if (this.sort) {
                this.tableDataSource.sort = this.sort;
            }
        });
    }

    private updateColumnsForSegment(segment: 'pending' | 'approved' | 'archived') {
        if (segment === 'pending') {
            this.tableColumns = [
                'name',
                'contact',
                'registration date',
                'status',
                'action',
            ];
        } else {
            this.tableColumns = [
                'name',
                'contact',
                'registration date',
                'status',
                'action',
            ];
        }
    }

    private filterBySegment(segment: 'pending' | 'approved' | 'archived') {
        let filtered: PractitionerWithStatus[] = [];

        if (segment === 'pending') {
            filtered = this.immutableTableData.filter(p => p.status === 'pending');
            this.pageTitle = 'Pending Practitioner Registrations';
            this.pageSubtitle = 'Practitioners awaiting approval';
        } else if (segment === 'approved') {
            filtered = this.immutableTableData.filter(p => p.status === 'approved');
            this.pageTitle = 'Approved Practitioners';
            this.pageSubtitle = 'Active practitioners in the system';
        } else if (segment === 'archived') {
            filtered = this.immutableTableData.filter(p => p.status === 'archived');
            this.pageTitle = 'Archived Practitioners';
            this.pageSubtitle = 'Deactivated practitioners';
        }

        filtered.sort((a, b) => {
            const dateA = new Date(a.submittedAt || a.createdAt || a.registrationDate || 0).getTime();
            const dateB = new Date(b.submittedAt || b.createdAt || b.registrationDate || 0).getTime();
            return dateB - dateA;
        });

        this.currentSegmentRows = filtered;
        this.applyLocalFilters(true);
    }

    onFilterChanged(change: { filterKey: string; values: string[] }): void {
        if (!change?.filterKey) {
            return;
        }

        const normalizedKey = change.filterKey.toLowerCase();
        const normalizedValues = (change.values ?? []).map(value => (value || '').toLowerCase()).filter(Boolean);
        this.activeFilters = {
            ...this.activeFilters,
            [normalizedKey]: normalizedValues
        };
        this.applyLocalFilters(true);
    }

    onSearchCriteriaChanged(change: { term: string; field: string | null }): void {
        this.activeSearchTerm = (change?.term ?? '').trim().toLowerCase();
        this.activeSearchField = change?.field || this.selectedSearchField;
        this.applyLocalFilters(true);
    }

    private applyLocalFilters(resetPage: boolean = false): void {
        const filtered = this.currentSegmentRows.filter(row => this.matchesLocalFilters(row));
        this.tableDataLevel2.next(filtered);
        this.tableDataSource.data = filtered;
    }

    private matchesLocalFilters(row: PractitionerWithStatus): boolean {
        if (!this.matchesSearch(row)) {
            return false;
        }

        const genderFilters = this.getFilterValues('gender');
        if (genderFilters.size) {
            const gender = (row.gender || '').toString().toLowerCase();
            if (!gender || !genderFilters.has(gender)) {
                return false;
            }
        }

        return true;
    }

    private matchesSearch(row: PractitionerWithStatus): boolean {
        if (!this.activeSearchTerm) {
            return true;
        }

        return this.getSearchValues(row, this.activeSearchField)
            .some(value => value.includes(this.activeSearchTerm));
    }

    private getSearchValues(row: PractitionerWithStatus, field: string | null): string[] {
        const normalize = (value: unknown) => (value ?? '').toString().toLowerCase();
        const name = this.getPractitionerName(row);
        const email = row.email || this.getTelecomValue(row, 'email');
        const phone = row.phoneNumber || this.getTelecomValue(row, 'phone');

        switch (field) {
            case 'practitionerId':
                return [this.currentSegment === 'approved' ? (row.fhirResourceId || row.id) : ''].map(normalize);
            case 'name':
                return [name].map(normalize);
            case 'email':
                return [email].map(normalize);
            case 'phone':
                return [phone].map(normalize);
            case 'gender':
                return [row.gender].map(normalize);
            default:
                return [
                    name,
                    email,
                    phone,
                    this.currentSegment === 'approved' ? (row.fhirResourceId || row.id) : ''
                ].map(normalize);
        }
    }

    private getFilterValues(key: string): Set<string> {
        return new Set(this.activeFilters[key.toLowerCase()] ?? []);
    }

    private getTelecomValue(row: PractitionerWithStatus, system: string): string {
        const telecom = Array.isArray(row.telecom) ? row.telecom : [];
        return telecom.find(item => item?.system === system)?.value ?? '';
    }

    private getPractitionerName(row: PractitionerWithStatus): string {
        return row.fullName ||
            `${row.firstName || ''} ${row.lastName || ''}`.trim() ||
            row.name?.[0]?.text ||
            `${row.name?.[0]?.given?.[0] || ''} ${row.name?.[0]?.family || ''}`.trim();
    }

    viewDetails(element: PractitionerWithStatus) {
        const ref = this.dialog.open(PractitionerDetailsComponent, {
            maxHeight: '93vh',
            maxWidth: '650px',
            data: this.stripSyntheticFields(element)
        });

        ref.afterClosed().subscribe((result) => {
            if (result?.approved && result?.id) {
                const index = this.immutableTableData.findIndex(p => p.id === result.id);
                if (index !== -1) {
                    this.immutableTableData[index] = {
                        ...this.immutableTableData[index],
                        active: true,
                        status: 'approved',
                        isApproved: true
                    };
                    this.filterBySegment(this.currentSegment);
                }
            } else if (result?.rejected && result?.id) {
                this.immutableTableData = this.immutableTableData.filter(p => p.id !== result.id);
                this.filterBySegment(this.currentSegment);

                this._sn.openFromComponent(SuccessMessageComponent, {
                    data: { message: 'Practitioner registration rejected and record deleted.' },
                    duration: 3500,
                });
            }
        });
    }

    // ✅ Updated archivePractitioner with dialog
    archivePractitioner(element: PractitionerWithStatus) {
        const displayName = element.fullName || 
                           `${element.firstName || ''} ${element.lastName || ''}`.trim() || 
                           'this practitioner';

        const dialogData: ConfirmActionDialogData = {
            title: 'Archive Practitioner',
            message: `Are you sure you want to archive this practitioner? They will be moved to the archived tab and will no longer have access to the system.`,
            actionType: 'archive',
            itemName: displayName,
            requiresReason: true,
            confirmButtonText: 'Archive',
            cancelButtonText: 'Cancel'
        };

        const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirmed) {
                this.performArchive(element, result.reason || 'No reason provided');
            }
        });
    }

    // ✅ Separate method to perform archive with correct fhirResourceId
    private performArchive(element: PractitionerWithStatus, reason: string) {
        // ✅ Use fhirResourceId from the element
        const fhirId = element.fhirResourceId || element.id;

        if (!fhirId) {
            this.errorService.openandCloseError('Cannot archive: FHIR Resource ID not found');
            return;
        }

        this.http.post(`${this.backendApi}/api/admin/practitioners/${fhirId}/archive`, { reason })
            .subscribe({
                next: () => {
                    // Update local state
                    const index = this.immutableTableData.findIndex(p => 
                        p.fhirResourceId === fhirId || p.id === fhirId
                    );
                    
                    if (index !== -1) {
                        this.immutableTableData[index] = {
                            ...this.immutableTableData[index],
                            active: false,
                            status: 'archived',
                            isApproved: false
                        };
                        this.filterBySegment(this.currentSegment);
                    }

                    this._sn.openFromComponent(SuccessMessageComponent, {
                        data: { message: 'Practitioner archived successfully' },
                        duration: 3000,
                    });
                },
                error: (err) => {
                    console.error('Archive error:', err);
                    this.errorService.openandCloseError(
                        err.error?.message || 'Failed to archive practitioner'
                    );
                }
            });
    }

    // ✅ Updated reactivatePractitioner with dialog
    reactivatePractitioner(element: PractitionerWithStatus) {
        const displayName = element.fullName || 
                           `${element.firstName || ''} ${element.lastName || ''}`.trim() || 
                           'this practitioner';

        const dialogData: ConfirmActionDialogData = {
            title: 'Reactivate Practitioner',
            message: `Are you sure you want to reactivate this practitioner? They will regain access to the system.`,
            actionType: 'reactivate',
            itemName: displayName,
            requiresReason: false,
            confirmButtonText: 'Reactivate',
            cancelButtonText: 'Cancel'
        };

        const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirmed) {
                this.performReactivate(element);
            }
        });
    }

    // ✅ Separate method to perform reactivate with correct fhirResourceId
    private performReactivate(element: PractitionerWithStatus) {
        // ✅ Use fhirResourceId from the element
        const fhirId = element.fhirResourceId || element.id;

        if (!fhirId) {
            this.errorService.openandCloseError('Cannot reactivate: FHIR Resource ID not found');
            return;
        }

        this.http.post(`${this.backendApi}/api/admin/practitioners/${fhirId}/reactivate`, {})
            .subscribe({
                next: () => {
                    // Update local state
                    const index = this.immutableTableData.findIndex(p => 
                        p.fhirResourceId === fhirId || p.id === fhirId
                    );
                    
                    if (index !== -1) {
                        this.immutableTableData[index] = {
                            ...this.immutableTableData[index],
                            active: true,
                            status: 'approved',
                            isApproved: true
                        };
                        this.filterBySegment(this.currentSegment);
                    }

                    this._sn.openFromComponent(SuccessMessageComponent, {
                        data: { message: 'Practitioner reactivated successfully' },
                        duration: 3000,
                    });
                },
                error: (err) => {
                    console.error('Reactivate error:', err);
                    this.errorService.openandCloseError(
                        err.error?.message || 'Failed to reactivate practitioner'
                    );
                }
            });
    }

  editPractitioner(element: PractitionerWithStatus) {
    const addr = (element as any).address?.[0] ?? {};

    const dialogRef = this.dialog.open(PractitionerEditDialogComponent, {
        width: '500px',
        data: {
            firstName: element.firstName,
            lastName: element.lastName,
            phoneNumber: element.phoneNumber,
            email: element.email,
            gender: element.gender as string,
            birthDate: element.birthDate,
            addressLine: addr.line?.[0],
            city: addr.city,
            state: addr.state,
            postalCode: addr.postalCode,
            country: addr.country
        }
    });

    dialogRef.afterClosed().subscribe(result => {
        if (result?.updated) {
            this.performPractitionerUpdate(element, result);
        }
    });
}

private performPractitionerUpdate(
    element: PractitionerWithStatus,
    updated: {
        firstName: string; lastName: string; phoneNumber: string;
        gender: string; birthDate: string;
        addressLine: string; city: string; state: string; postalCode: string; country: string;
    }
) {
    const fhirId = element.fhirResourceId || element.id;

    if (!fhirId) {
        this.errorService.openandCloseError('Cannot update: FHIR Resource ID not found');
        return;
    }

    this.http.put(`${this.backendApi}/api/admin/practitioners/${fhirId}`, {
        firstName: updated.firstName,
        lastName: updated.lastName,
        phoneNumber: updated.phoneNumber,
        gender: updated.gender,
        birthDate: updated.birthDate,
        addressLine: updated.addressLine,
        city: updated.city,
        state: updated.state,
        postalCode: updated.postalCode,
        country: updated.country
    }).subscribe({
        next: () => {
            const index = this.immutableTableData.findIndex(p =>
                p.fhirResourceId === fhirId || p.id === fhirId
            );

            if (index !== -1) {
                this.immutableTableData[index] = {
                    ...this.immutableTableData[index],
                    firstName: updated.firstName,
                    lastName: updated.lastName,
                    phoneNumber: updated.phoneNumber,
                    fullName: `${updated.firstName} ${updated.lastName}`.trim(),
                    gender: updated.gender as any,
                    birthDate: updated.birthDate
                };
                this.filterBySegment(this.currentSegment);
            }

            this._sn.openFromComponent(SuccessMessageComponent, {
                data: { message: 'Practitioner updated successfully' },
                duration: 3000,
            });
        },
        error: (err) => {
            console.error('Update error:', err);
            this.errorService.openandCloseError(
                err.error?.message || 'Failed to update practitioner'
            );
        }
    });
}

    showRow(row: any) {
        console.log(row);
    }

    canAddPractitionerRegistration(): boolean {
        return this.auth.can('practitioner', 'add');
    }

    private stripSyntheticFields(p: any): Practitioner {
        const { status, userId, email, isApproved, phoneNumber, fullName, firstName, lastName, createdAt, submittedAt, registrationDate, ...rest } = p || {};
        return rest as Practitioner;
    }
}
